import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useConfirm } from 'hooks/useConfirm';
import { getFieldsHiddenByCriteria } from 'lib/fieldCriteria';
import { getFieldGroupInfo, hasValue } from 'lib/fields';
import { randomKey } from 'lib/riskbase';
import React from 'react';
import { Field } from 'screens/AssetScreen/Field/Field';
import { FieldTypes } from 'store/assets/types';
import { FieldGroup } from 'store/organisations/types';
import { PinNew, PinNewWithName } from 'store/plan/types';
import { AppContext, FieldInstance, FieldResult } from 'types/types';

type Props = {
  context: AppContext;
  id: string;
  editableFieldIds: string[];
  requiredFieldIds: string[];
  hiddenFieldIds: string[];
  group: Omit<FieldGroup, 'fields'> & {
    calculatedInfo: ReturnType<typeof getFieldGroupInfo>;
  };
  fields: FieldGroup['fields'];
  isNew?: boolean;
  promptTitle?: string;
  results: Record<string, FieldResult>;
  resultsOld?: Record<string, FieldResult>;
  resultsNew?: Record<string, FieldResult>;
  otherInstances: FieldInstance[];
  onChange: (
    fieldId: string,
    value: string | string[] | null,
    pin?: PinNew | '' | null,
  ) => void;
  onPressSaveButton?: () => void;
  onPressCancelButton?: () => void;
  showInvalid: boolean;
};

export const GlobalFieldLightRepeatableInstanceScreen: React.FC<Props> = ({
  context,
  id,
  group,
  fields,
  isNew,
  editableFieldIds = [],
  requiredFieldIds = [],
  hiddenFieldIds = [],
  promptTitle,
  results,
  resultsOld,
  resultsNew,
  onChange,
  otherInstances,
  onPressSaveButton,
  onPressCancelButton,
  showInvalid,
}) => {
  const confirm = useConfirm();

  const { fieldIds } = group;
  const fieldKeys = React.useRef(
    group.fieldIds.reduce<Record<string, string>>((acc, fieldId) => {
      acc[fieldId] = `${fieldId}-${Date.now()}`;
      return acc;
    }, {}),
  );

  const otherInstanceData = React.useMemo(
    () =>
      otherInstances.reduce<{
        pins: PinNewWithName[];
        textSuggestionsByField: Record<string, string[]>;
      }>(
        (acc, oInstance) =>
          Object.values(oInstance.results).reduce((acc1, result) => {
            if (oInstance.instanceId !== id) {
              const { fieldId } = result;
              const field = fields[fieldId];
              switch (field?.type) {
                case FieldTypes.TEXT:
                case FieldTypes.NOTE:
                  if (result.value) {
                    if (!acc1.textSuggestionsByField[fieldId]) {
                      acc1.textSuggestionsByField[fieldId] = [];
                    }
                    acc1.textSuggestionsByField[fieldId]!.push(
                      result.value as string,
                    );
                  }
                  break;
                default:
              }
            }
            return acc;
          }, acc),
        { pins: [], textSuggestionsByField: {} },
      ),
    [fields, id, otherInstances],
  );

  const handleRevert = React.useCallback(
    (fieldId: string) => {
      confirm({
        text: "This field has been changed in this assessment. Continuing will revert the value back to the asset's value",
        confirmText: 'Revert',
        destructive: true,
        onConfirm: () => {
          fieldKeys.current[fieldId] = `${fieldId}-${randomKey()}`;
          onChange(fieldId, null, null);
        },
      });
    },
    [confirm, onChange],
  );

  const fieldsHaveCriteria = React.useMemo(
    () => group.fieldIds.some((fieldId) => !!fields[fieldId]?.criteria.length),
    [group, fields],
  );

  const fieldIsHiddenByCriteria =
    group && fieldsHaveCriteria
      ? getFieldsHiddenByCriteria({
          fieldIds: group.fieldIds,
          fields,
          results,
        })
      : {};

  const fieldIdsForLayout: string[] = fieldIds.filter(
    (fieldId) =>
      !hiddenFieldIds.includes(fieldId) &&
      (!fieldIsHiddenByCriteria[fieldId] || hasValue(results[fieldId])),
  );

  return (
    <PageScrollView
      header={
        <NavigationHeader
          onPressSaveButton={onPressSaveButton}
          onPressCancelButton={onPressCancelButton}
          heading={`${isNew ? 'Add ' : ''}${group.calculatedInfo.singularName}`}
          subheading={promptTitle}
        />
      }
    >
      {fieldIdsForLayout.map((fieldId) => {
        const field = fields[fieldId]!;
        return (
          <Field
            key={fieldKeys.current[field.id]!}
            context={context}
            field={field}
            value={results[field.id]?.value}
            pin={results[field.id]?.pin || undefined}
            showRevert={!!resultsNew?.[field.id]}
            previousValue={resultsOld?.[field.id]?.value}
            previousPin={resultsOld?.[field.id]?.pin || undefined}
            textSuggestions={otherInstanceData.textSuggestionsByField[field.id]}
            instanceId={id}
            editable={editableFieldIds.includes(field.id)}
            required={requiredFieldIds.includes(field.id)}
            shouldBeHidden={!!fieldIsHiddenByCriteria[field.id]}
            onChange={onChange}
            onRevert={handleRevert}
            onEsc={onPressCancelButton}
            showInvalid={showInvalid}
          />
        );
      })}
    </PageScrollView>
  );
};
